import { DeviceMobileIcon, IdentificationIcon } from '@heroicons/react/outline';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import ConfettiIcon from 'components/atoms/SVG/ConfettiIcon.component';
import GivingCoinsIcon from 'components/atoms/SVG/GivingCoinsIcon.component';
import ModalHeading from 'components/molecules/ModalHeading/ModalHeading.component';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { WEBAPP_BANKING_ONBOARDING_IDENTITYCONFIRMATION_CLICKCONFIRM } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';
import type { ActivationStepProps } from '../../ActivationFlow/types';

const ConfirmIdentity: FC<
  Pick<
    ActivationStepProps,
    | 'progressSegments'
    | 'onProgressSegmentClick'
    | 'showProgressBar'
    | 'showBackButton'
    | 'onBack'
  > & {
    isConfirming?: boolean;
    onConfirm?: () => void;
  }
> = ({
  isConfirming,
  onConfirm,
  progressSegments,
  onProgressSegmentClick,
  showProgressBar,
  showBackButton,
  onBack,
}) => {
  const { t } = useTranslation();

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      showBackButton={showBackButton}
      onBack={onBack}
    >
      <motion.div
        {...fadeTransition}
        className="flex items-center justify-center flex-col relative h-auto"
        key="8"
      >
        <div className="w-[80%]">
          <ModalHeading
            Icon={ConfettiIcon}
            title={t(
              'accountable_banking.onboarding.identity_confirmation.title',
            )}
            subtitle={t(
              'accountable_banking.onboarding.identity_confirmation.subtitle',
            )}
            subtitleClassName="justify-self-start !ml-0"
          />
        </div>

        <div className="w-[80%] mx-auto my-4">
          <h3 className="text-primary-700">
            {t(
              'accountable_banking.onboarding.identity_confirmation.requirements.heading',
            )}
          </h3>

          <div className="flex items-center space-x-4 my-3">
            <div className="w-10 h-10 rounded-full bg-bank-100 p-2">
              <IdentificationIcon className="text-bank" />
            </div>

            <div className="text-left">
              <h3 className="font-bold text-primary-800">
                {t(
                  'accountable_banking.onboarding.identity_confirmation.requirements.passport',
                )}
              </h3>
            </div>
          </div>

          <div className="flex items-center space-x-4 my-3">
            <div className="w-10 h-10 rounded-full bg-bank-100 p-2">
              <DeviceMobileIcon className="text-bank" />
            </div>

            <div className="text-left">
              <h3 className="font-bold text-primary-800">
                {t(
                  'accountable_banking.onboarding.identity_confirmation.requirements.selfie',
                )}
              </h3>
            </div>
          </div>
        </div>

        <Modal
          className="!max-w-[40rem]"
          name="Swan & BNP Partnership Modal"
          trigger={(_api) => (
            <div className="w-[80%] space-y-3">
              <p className="text-primary-700">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <p className="text-primary-700">{children}</p>
                    ),
                    a: ({ children, href }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-700 underline"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {t('wallet.tutorial.slide_f.explanation.subtitle')}
                </ReactMarkdown>
              </p>
            </div>
          )}
          render={(props) => (
            <div className="pt-5">
              <ModalHeading
                Icon={GivingCoinsIcon}
                title={t('wallet.tutorial.slide_f.partnership.title')}
              />

              <div className="w-[80%] mt-10 mx-auto">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <p className="text-primary-700">{children}</p>
                    ),
                    a: ({ children, href }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-700 underline"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {t(
                    'accountable_banking.onboarding.swan_partnership.modal.content',
                  )}
                </ReactMarkdown>

                <Button className="w-full mt-7" onClick={props.closeModal}>
                  {t('close')}
                </Button>
              </div>
            </div>
          )}
        />

        <div className="w-full flex justify-center">
          <Button
            color="bank"
            className="mt-10 w-[80%]"
            loading={isConfirming}
            tracingEvent={
              WEBAPP_BANKING_ONBOARDING_IDENTITYCONFIRMATION_CLICKCONFIRM
            }
            onClick={onConfirm}
          >
            {t(
              'accountable_banking.onboarding.identity_confirmation.confirm_cta',
            )}
          </Button>
        </div>
      </motion.div>
    </OnboardingStepWrapper>
  );
};

export default ConfirmIdentity;
